/**
 * .tuMetrics {"tu1": 0.93, "tu2": 0.93, "tu3": 2, "notes": "Pure: one meeting-options shape for the modal, the Meet dialog, chat_sessions.meeting_options and company_calendar_events", "tuVersion": "2.5"}
 * .help "What a meeting knows about itself besides its name: when, how long (30 by default), which project, show on calendar, recurrence, Google. Converts between the UI shape and the jsonb column, and computes the planned window."
 */

export const DEFAULT_MEETING_DURATION_MINUTES = 30;
export const MEETING_DURATION_CHOICES = [15, 30, 45, 60, 90, 120] as const;

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

/** UI-side shape. Every field optional so callers that only know the name still work. */
export interface MeetingOptions {
  showOnTeamCalendar?: boolean;
  isRecurring?: boolean;
  recurrenceFrequency?: RecurrenceFrequency;
  /** HH:MM, recurring meetings only. */
  standardTime?: string;
  /** 0–6, weekly/monthly only. */
  recurrenceDays?: number[];
  externalParticipants?: string[];
  /** Planned length. Real start/end come from the live activity. */
  durationMinutes?: number;
  /** ISO. Absent means "now". */
  scheduledStart?: string;
  /** Project this meeting is about. Join inherits it. */
  projectId?: string | null;
  /** Mirror to the creator's Google Calendar. */
  addToGoogle?: boolean;
  /** Set once the Google event exists. */
  googleEventId?: string | null;
}

/** jsonb column shape (snake_case). Kept flat so SQL can read it. */
export interface MeetingOptionsRow {
  show_on_team_calendar?: boolean;
  is_recurring?: boolean;
  recurrence?: { frequency: RecurrenceFrequency; standard_time?: string; days?: number[] } | null;
  external_participants?: string[];
  duration_minutes?: number;
  scheduled_start?: string | null;
  add_to_google?: boolean;
  google_event_id?: string | null;
}

function cleanMinutes(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : NaN;
  return v > 0 ? v : DEFAULT_MEETING_DURATION_MINUTES;
}

export function meetingOptionsToRow(o: MeetingOptions): MeetingOptionsRow {
  const row: MeetingOptionsRow = {
    show_on_team_calendar: o.showOnTeamCalendar === true,
    is_recurring: o.isRecurring === true,
    recurrence:
      o.isRecurring && o.recurrenceFrequency
        ? {
            frequency: o.recurrenceFrequency,
            standard_time: o.standardTime,
            days:
              o.recurrenceFrequency === 'daily' ? undefined : (o.recurrenceDays ?? []).filter((d) => d >= 0 && d <= 6),
          }
        : null,
    external_participants: (o.externalParticipants ?? []).map((e) => e.trim()).filter((e) => e.includes('@')),
    duration_minutes: cleanMinutes(o.durationMinutes),
    scheduled_start: o.scheduledStart ?? null,
    add_to_google: o.addToGoogle === true,
  };
  if (o.googleEventId !== undefined) row.google_event_id = o.googleEventId;
  return row;
}

export function meetingOptionsFromRow(row: MeetingOptionsRow | null | undefined): MeetingOptions {
  if (!row || typeof row !== 'object') return {};
  const rec = row.recurrence ?? null;
  return {
    showOnTeamCalendar: row.show_on_team_calendar === true,
    isRecurring: row.is_recurring === true,
    recurrenceFrequency: rec?.frequency,
    standardTime: rec?.standard_time,
    recurrenceDays: rec?.days,
    externalParticipants: row.external_participants ?? [],
    durationMinutes: cleanMinutes(row.duration_minutes),
    scheduledStart: row.scheduled_start ?? undefined,
    addToGoogle: row.add_to_google === true,
    googleEventId: row.google_event_id ?? null,
  };
}

/** Next quarter hour at or after `nowMs`. 10:07 → 10:15, 10:15 → 10:15. */
export function nextQuarterHour(nowMs: number): Date {
  const d = new Date(nowMs);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  const up = Math.ceil(m / 15) * 15;
  d.setMinutes(up);
  return d;
}

/** Planned block for the calendar row. Start = scheduled or now; end = start + duration. */
export function plannedMeetingWindow(
  o: Pick<MeetingOptions, 'scheduledStart' | 'durationMinutes'>,
  nowMs: number,
): { startIso: string; endIso: string; durationMinutes: number } {
  const parsed = o.scheduledStart ? new Date(o.scheduledStart).getTime() : NaN;
  const startMs = Number.isFinite(parsed) ? parsed : nowMs;
  const durationMinutes = cleanMinutes(o.durationMinutes);
  return {
    startIso: new Date(startMs).toISOString(),
    endIso: new Date(startMs + durationMinutes * 60_000).toISOString(),
    durationMinutes,
  };
}

/** Value for `<input type="datetime-local">` in the browser's local zone. */
export function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse a datetime-local string back to ISO; empty/invalid → undefined (meaning now). */
export function fromDatetimeLocal(value: string): string | undefined {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? new Date(ms).toISOString() : undefined;
}

/**
 * Real end for the calendar row when an attendee's meeting activity ends.
 * First one to leave closes the row at `now`; later leavers extend it if they stayed longer.
 * `status` is how we tell a planned end from a real one.
 */
export function realEndForCalendarRow(
  row: { status: string; end_time?: string | null },
  nowMs: number,
): { end_time: string; status: 'completed' } | null {
  const nowIso = new Date(nowMs).toISOString();
  if (row.status !== 'completed') return { end_time: nowIso, status: 'completed' };
  const prev = row.end_time ? new Date(row.end_time).getTime() : 0;
  if (nowMs > prev) return { end_time: nowIso, status: 'completed' };
  return null;
}

/**
 * Real start for the calendar row when the first attendee joins: the row's start moves to `now`
 * only while it is still `scheduled` (nobody joined yet). Joining ten minutes early starts the block early.
 */
export function realStartForCalendarRow(
  row: { status: string },
  nowMs: number,
): { start_time: string; status: 'active' } | null {
  if (row.status !== 'scheduled') return null;
  return { start_time: new Date(nowMs).toISOString(), status: 'active' };
}
